import type { Language } from '@seek/ca-myprofile-utils';
import { Button, Actions, Text, Stack } from 'braid-design-system';
import React, { Component, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import loadable from 'sku/@loadable/component';

import {
  updateCreateStep,
  returnToNonProfilePage,
} from 'src/components/Profile/CreateResume/actionCreators';
import { CreateStep } from 'src/components/Profile/CreateResume/reducer';
import {
  RESUME,
  CREATE_RESUME_PAGE,
  PROFILE_PAGE,
} from 'src/components/Router';
import { Link } from 'src/components/Router/Link';
import { READ } from 'src/consts';
import { getCurrentPage } from 'src/middlewares/utils';
import { useAppConfig } from 'src/modules/appConfig';
import { hotjarEvent } from 'src/modules/hotjar';
import type { Action, ReduxState } from 'src/types';

import { LoadableFallback } from '../../Loading/LoadableFallback';
import { resumeEditViewLoaded } from '../actionCreators';
import { useResumes } from '../data';
import type { Resume, FileStatus } from '../types';

import { AnimationTimings } from './FileUpload/utils';
import { ManageResumes } from './ManageResumes/ManageResumes';
import { ParsedDataAlert } from './ParsedDataAlert/ParsedDataAlert';
import { getTranslation } from './translation';

const FileUpload = loadable(() => import('./FileUpload/FileUpload'), {
  fallback: <LoadableFallback />,
  resolveComponent: (components) => components.FileUpload,
});
FileUpload.preload();

interface StoreProps {
  resumeFileStatus: FileStatus;
  rolesFound: number;
  qualificationsFound: number;
  pendingResumeId?: string;
}

interface HookProps {
  resumes: Resume[];
  language: Language;
  resumeAnimationPlaying: boolean | null;
  setResumeAnimationPlaying: (isPlaying: boolean | null) => void;
}

interface DispatchProps {
  resumeEditViewLoaded: () => Action;
  updateCreateStep: (step: CreateStep) => void;
  returnToNonProfilePage: () => Action;
}

interface State {
  showParsedDataAlert: boolean;
}

type Props = StoreProps & HookProps & DispatchProps;

/**
 * Due to we are using the getSnapshotBeforeUpdate
 * there’s actually no way to convert this to functional component right now
 * for more detail please visit https://github.com/facebook/react/issues/15221
 **/
class EditResumeComponent extends Component<Props, State> {
  ref: React.RefObject<HTMLDivElement>;

  constructor(props: Props) {
    super(props);

    this.ref = React.createRef();

    this.state = {
      showParsedDataAlert: false,
    };
  }

  componentDidMount() {
    this.props.resumeEditViewLoaded();

    if (getCurrentPage() === CREATE_RESUME_PAGE) {
      window.scrollTo(0, 0);
    }
  }

  createAnother = () => {
    this.props.updateCreateStep(CreateStep.PROFILE_REVIEW);
    window.scrollTo(0, 0);
  };

  dismissParsedDataAlert = () => {
    this.setState({
      showParsedDataAlert: false,
    });
  };

  getSnapshotBeforeUpdate(prevProps: Props) {
    if (
      prevProps.rolesFound !== this.props.rolesFound ||
      prevProps.qualificationsFound !== this.props.qualificationsFound
    ) {
      return {
        bodyHeight: document.body.clientHeight,
        scrollY: window.scrollY,
      };
    }
    return false;
  }

  componentDidUpdate(
    prevProps: Props,
    _: any,
    prevWindowState: {
      bodyHeight: number;
      scrollY: number;
    } | null,
  ) {
    if (
      (prevProps.rolesFound < this.props.rolesFound ||
        prevProps.qualificationsFound < this.props.qualificationsFound) &&
      prevWindowState !== null
    ) {
      // Adjust the scroll position of the page to account for content that has
      // been inserted above so the page does not jump
      const getNewScrollPosition = () =>
        prevWindowState.scrollY +
        document.body.clientHeight -
        prevWindowState.bodyHeight;
      window.scrollTo(0, getNewScrollPosition());

      // The ReadMore component unfortunately re-renders after being mounted due
      // to needing to calculate height after a ref has been created. The scroll
      // needs to be run again to adjust for any further height changes to the
      // elements above EditResume
      setTimeout(() => {
        window.scrollTo(0, getNewScrollPosition());
      }, 0);
      setTimeout(() => {
        this.setState({
          showParsedDataAlert: true,
        });
      }, AnimationTimings.SUCCESS + AnimationTimings.LAST_FRAME);
    }
  }

  render() {
    const {
      resumes,
      resumeFileStatus,
      rolesFound,
      qualificationsFound,
      pendingResumeId,
      language,
    } = this.props;
    const { showParsedDataAlert } = this.state;

    const page = getCurrentPage();

    const parsedResume = resumes.find(
      (resume) => resume.id === pendingResumeId,
    );
    const isMaxResumesReached = resumes.length >= 10;

    const returnButtonText = getTranslation(language, 'Return to profile');
    const hasParsedData = rolesFound !== 0 || qualificationsFound !== 0;

    return (
      <Stack space="xlarge">
        <div ref={this.ref} data-automation="edit-resume-text">
          <Text>
            {getTranslation(language, 'Add up to 10 resumés. Accepted...')}
          </Text>
        </div>
        {hasParsedData &&
        showParsedDataAlert &&
        page === PROFILE_PAGE &&
        !this.props.resumeAnimationPlaying ? (
          <ParsedDataAlert
            resumeName={parsedResume?.fileMetadata.name || ''}
            rolesFound={rolesFound}
            qualificationsFound={qualificationsFound}
            onClose={this.dismissParsedDataAlert}
          />
        ) : (
          <FileUpload
            maxResumes={isMaxResumesReached}
            resumes={resumes}
            resumeFileStatus={resumeFileStatus}
            resumeAnimationPlaying={this.props.resumeAnimationPlaying}
            setResumeAnimationPlaying={this.props.setResumeAnimationPlaying}
          />
        )}
        <ManageResumes />
        {page === CREATE_RESUME_PAGE && (
          <Actions>
            <Link
              page={PROFILE_PAGE}
              component={Button}
              tone="formAccent"
              data={{
                automation: 'resume-return-button',
              }}
            >
              {returnButtonText}
            </Link>
            <Button
              onClick={this.createAnother}
              data={{ automation: 'create-another-resume' }}
              tone="formAccent"
              variant="transparent"
            >
              {getTranslation(language, 'Create another')}
            </Button>
          </Actions>
        )}
        {page !== CREATE_RESUME_PAGE && (
          <Actions>
            <Link
              card={RESUME}
              mode={READ}
              component={Button}
              tone="formAccent"
              data={{
                automation: 'manage-resume-done',
              }}
            >
              {getTranslation(language, 'Done')}
            </Link>
          </Actions>
        )}
      </Stack>
    );
  }
}

const mapStateToProps = ({
  resume: {
    parsing: { rolesFound, qualificationsFound, pendingResumeId },
    resumeFileStatus,
  },
}: ReduxState): StoreProps => ({
  rolesFound,
  qualificationsFound,
  pendingResumeId,
  resumeFileStatus,
});

const mapDispatchToProps = {
  resumeEditViewLoaded,
  updateCreateStep,
  returnToNonProfilePage,
};

const withHookData =
  (WrappedComponent: React.ComponentType<Props>) =>
  (props: StoreProps & DispatchProps) => {
    const { resumes } = useResumes();
    const { language } = useAppConfig();
    const hasResumeWithSensitiveInfo = resumes.some((resume) =>
      Boolean(resume.fileMetadata.sensitiveDataInfo?.isDetected),
    );
    useEffect(() => {
      if (hasResumeWithSensitiveInfo) {
        hotjarEvent('profile-resume-with-sensitive-info');
      }
    }, [hasResumeWithSensitiveInfo]);
    const [resumeAnimationPlaying, setResumeAnimationPlaying] = useState<
      null | boolean
    >(null);

    return (
      <WrappedComponent
        {...props}
        resumes={resumes}
        language={language}
        resumeAnimationPlaying={resumeAnimationPlaying}
        setResumeAnimationPlaying={setResumeAnimationPlaying}
      />
    );
  };

const connectFn = connect(mapStateToProps, mapDispatchToProps, null, {
  pure: false,
});

export const EditResume = connectFn(withHookData(EditResumeComponent));
